import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DayPicker from 'react-day-picker'
import 'react-day-picker/lib/style.css'
import { useContextStatistics } from '../../contexts/statisticsContext'
import { formatDate } from '../../utils/date'
import { initialRangeState } from '../../utils/statistics'
import {
    fromDateInputValue,
    getLastWeekRange,
    getRangeAfterDayClick,
    getRangeAfterDayHover,
    getRangeWithDate,
    getWeekRangeOf,
    isRangeComplete,
    isSelectingLastDay,
    toDateInputValue,
} from '../../utils/calendarRange'
import StatisticsCalendarSelection from '../StatisticsCalendarSelection'
import YearMonthForm from './YearMonthForm'
import styles from './index.module.css'

const currentYear = new Date().getFullYear()
const fromMonth = new Date(2006, 0, 1)
const toMonth = new Date(currentYear, 11)

const getFirstOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)

/* The browser decides how <input type="date"> displays its value (based on the computer's regional settings),
so the date is shown as "05 Mar 2024" in a button, and the real input - kept invisible - only opens the native picker. */
const DateField = ({ label, date, onChange }) => {
    const inputRef = useRef(null)
    const displayDate = date ? formatDate(new Date(date), '%d %b %Y') : 'Select date'

    const openPicker = () => {
        const input = inputRef.current
        if (!input) return

        if (typeof input.showPicker === 'function') {
            try {
                input.showPicker()
                return
            } catch (error) {
                // the browser refused to open the picker - fall back to focusing the input
            }
        }

        input.focus()
        input.click()
    }

    return (
        <div className={styles['date-field']}>
            <span>{label}</span>
            <div className={styles['date-picker']}>
                <button
                    type='button'
                    className={styles['date-label']}
                    onClick={openPicker}
                    aria-label={`${label} date: ${displayDate}. Open date picker`}
                >
                    {displayDate}
                    <span aria-hidden='true'>📅</span>
                </button>
                <input
                    ref={inputRef}
                    type='date'
                    tabIndex={-1}
                    aria-hidden='true'
                    className={styles['hidden-input']}
                    value={toDateInputValue(date)}
                    onChange={onChange}
                />
            </div>
        </div>
    )
}

const CalendarStatistics = () => {
    const [month, setMonth] = useState(getFirstOfMonth(new Date()))
    const isInitialRender = useRef(true)
    const { statisticsState, setSelectedRange, addNewCalendarRange } = useContextStatistics()
    const { selectedRange } = statisticsState
    const currSelectedRange = selectedRange[selectedRange.length - 1]

    // selects a range and shows its first month in the calendar
    const selectRange = useCallback((range) => {
        setSelectedRange(range)
        if (range.from) {
            setMonth(getFirstOfMonth(range.from))
        }
    }, [setSelectedRange])

    useEffect(() => {
        if (isInitialRender.current) {
            selectRange(getLastWeekRange())
            isInitialRender.current = false
        }
    }, [selectRange])

    // ranges added before the current one are highlighted, but can not be edited in the calendar
    const otherRanges = useMemo(() => selectedRange
        .slice(0, -1)
        .filter(isRangeComplete)
        .map(range => ({ from: new Date(range.from), to: new Date(range.hoverTo) })),
    [selectedRange])

    // the month/year dropdowns, arrows and "Go to today" only move the calendar - they never change the selection
    const handleNavigation = (date) => setMonth(getFirstOfMonth(date))

    const handleDayClick = (day) => {
        setSelectedRange(getRangeAfterDayClick(currSelectedRange, day))
    }

    const handleDayMouseEnter = (day) => {
        const previewRange = getRangeAfterDayHover(currSelectedRange, day)
        if (previewRange) {
            setSelectedRange(previewRange)
        }
    }

    const handleWeekClick = (_, days) => {
        setSelectedRange(getWeekRangeOf(days[0]))
    }

    const handleDateInput = (field) => (e) => {
        const date = fromDateInputValue(e.target.value)
        if (date) {
            selectRange(getRangeWithDate(currSelectedRange, field, date))
        }
    }

    const handleClear = () => setSelectedRange(initialRangeState)

    // moves the calendar back to a range (e.g. after browsing to another year) without changing the selection
    const showRangeInCalendar = (range) => {
        if (range && range.from) {
            handleNavigation(new Date(range.from))
        }
    }

    const renderCaption = ({ date, localeUtils }) => (
        <YearMonthForm
            date={date}
            localeUtils={localeUtils}
            onMonthChange={handleNavigation}
            onYearChange={handleNavigation}
            fromMonth={fromMonth}
            toMonth={toMonth}
        />
    )

    const { from, hoverTo, enteredTo } = currSelectedRange

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <button
                    onClick={() => showRangeInCalendar(currSelectedRange)}
                    disabled={!from}
                    title={from ? 'Show the selected period in the calendar' : 'No period is selected'}
                >
                    Go to selection
                </button>
                <button
                    onClick={handleClear}
                    disabled={!from}
                    title={from ? 'Clear the period being edited' : 'No period is selected'}
                >
                    Clear selection
                </button>
            </div>

            <DayPicker
                className={styles.calendar}
                showWeekNumbers
                showOutsideDays
                firstDayOfWeek={1}
                selectedDays={[from, { from, to: enteredTo }]}
                modifiers={{ start: from, end: enteredTo, otherRange: otherRanges }}
                onDayClick={handleDayClick}
                onDayMouseEnter={handleDayMouseEnter}
                onWeekClick={handleWeekClick}
                renderWeek={(weekNumber) => <span title={`Select week ${weekNumber}`}>{weekNumber}</span>}
                todayButton='Go to today'
                onTodayButtonClick={handleNavigation}
                month={month}
                onMonthChange={handleNavigation}
                fromMonth={fromMonth}
                toMonth={toMonth}
                captionElement={renderCaption}
            />

            <div className={styles.selection}>
                <div className={styles.summary}>
                    {isRangeComplete(currSelectedRange) && (
                        <span>
                            Selected: <b>{formatDate(new Date(from), '%d %b %Y')} – {formatDate(new Date(hoverTo), '%d %b %Y')}</b>
                        </span>
                    )}
                    {!from && <span>Click the first day or a week number.</span>}
                    {isSelectingLastDay(currSelectedRange) && <span>Now click the last day.</span>}
                </div>

                <div className={styles.inputs}>
                    <DateField label='From' date={from} onChange={handleDateInput('from')} />
                    <DateField label='To' date={hoverTo} onChange={handleDateInput('to')} />
                </div>

                <StatisticsCalendarSelection onShowRange={showRangeInCalendar} />

                {isRangeComplete(currSelectedRange) && (
                    <div>
                        <button onClick={addNewCalendarRange}>Add another range</button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CalendarStatistics
